import type { ModalService } from './../ricos-modals';
import type {
  KeyboardShortcut,
  KeyboardShortcutId,
  LocalizedDisplayData,
  Platform,
  PublicCommands,
  TFunction,
  TiptapEditor,
} from './../ricos-types';

import { Keys } from './keys';

/**
 * Represents a keyboard shortcut in Ricos Editor.
 * Admits key combination, command, relevant context, display data.
 */
export type Shortcut = EditorKeyboardShortcut;

export class EditorKeyboardShortcut {
  shortcut: KeyboardShortcut;

  keys: { macOs: Keys; windows: Keys };

  static of(shortcut: KeyboardShortcut) {
    return new EditorKeyboardShortcut(shortcut);
  }

  private constructor(shortcut: KeyboardShortcut) {
    this.shortcut = shortcut;
    this.keys = {
      macOs: Keys.parse(shortcut.keys.macOs),
      windows: Keys.parse(shortcut.keys.windows),
    };
  }

  private getKeyCombinationText(platform: Platform): string {
    return this.keys[platform].toPlatformString(platform);
  }

  private getTooltipHint(platform: Platform): string {
    return this.keys[platform].toTooltipHint(platform);
  }

  /**
   * KeyboardShortcut config
   */
  getKeyboardShortcut(): KeyboardShortcut {
    return this.shortcut;
  }

  /**
   * Parsed Keys
   */
  getKeys(platform: Platform): Keys {
    return this.keys[platform];
  }

  /**
   * Command to run
   */
  getCommand(): (args: {
    publicCommands: PublicCommands;
    editor: TiptapEditor;
    modalService: ModalService;
    event?: KeyboardEvent;
  }) => void {
    return this.shortcut.command;
  }

  /**
   * Enabled status
   */
  isEnabled(): boolean {
    return this.shortcut.enabled;
  }

  /**
   * Pre-localized name
   */
  getName(): KeyboardShortcutId {
    return this.shortcut.name;
  }

  /**
   * Pre-localized group
   */
  getGroup(): string {
    return this.shortcut.group;
  }

  /**
   * Localized shortcut data for tooltips, help dialogs, etc
   */
  getDisplayData(t: TFunction, platform: Platform): LocalizedDisplayData {
    return {
      name: t(this.shortcut.name) as LocalizedDisplayData['name'],
      description: t(this.shortcut.description),
      group: t(this.shortcut.group),
      keyCombinationText: this.getKeyCombinationText(platform),
      tooltipHint: this.getTooltipHint(platform),
    };
  }

  /**
   * Reconfigure Shortcut
   */
  configure(config: Partial<KeyboardShortcut>): EditorKeyboardShortcut {
    return new EditorKeyboardShortcut({ ...this.shortcut, ...config });
  }

  /**
   * Determines whether shortcut equals to another shortcut, based on keys and group
   */
  equals(shortcut: Shortcut): boolean {
    return (
      this.keys.macOs.equals(shortcut.getKeys('macOs')) &&
      this.keys.windows.equals(shortcut.getKeys('windows')) &&
      !this.keys.macOs.equals(Keys.parse('Arrow')) &&
      !this.keys.windows.equals(Keys.parse('Arrow')) &&
      this.getGroup() === shortcut.getGroup()
    );
  }
}
